/* Petifo product pages: native Inbox launcher above the native sticky bar.
 * Move the real host, not a private shadow button: Inbox replaces that button
 * with a nested agent runtime after the first conversation is opened.
 * The native component owns data-stuck; this bridge never forces bar styles.
 */
(() => {
  const HOSTS = 'shopify-chat, shopify-agent, inbox-online-store-chat';
  const BAR = '#MainContent[data-template^="product"] sticky-add-to-cart [ref="stickyBar"]';
  const saved = new Map();
  let bar = null;
  let frame = 0;
  let lastOpen;

  function schedule() {
    if (!frame) frame = requestAnimationFrame(update);
  }

  function restore(host) {
    const previous = saved.get(host);
    if (!previous) return;
    if (previous.value) host.style.setProperty('transform', previous.value, previous.priority);
    else host.style.removeProperty('transform');
    host.removeAttribute('data-pf-chat-positioned');
    saved.delete(host);
  }

  function update() {
    frame = 0;
    const hosts = [...document.querySelectorAll(HOSTS)];
    const open = !!document.querySelector(
      'shopify-chat[open], shopify-agent[open], inbox-online-store-chat[is-open="true"], #chat-drawer[open]'
    );
    // Notify before measuring: closing Inbox restores the native sticky state.
    if (open !== lastOpen) {
      lastOpen = open;
      document.dispatchEvent(new CustomEvent('pf:chat-state-change'));
    }
    const productPage = !!document.querySelector('#MainContent[data-template^="product"]');
    const stuck = productPage && bar?.dataset.stuck === 'true' && !open;
    // Measure the final footprint, not the animated top during slide-in.
    const lift = stuck
      ? bar.getBoundingClientRect().height + Math.max(0, parseFloat(getComputedStyle(bar).bottom) || 0)
      : 0;

    for (const host of saved.keys()) {
      if (!host.isConnected || !hosts.includes(host)) restore(host);
    }
    for (const host of hosts) {
      if (!productPage || open) {
        restore(host);
        continue;
      }
      // Compensate the preview-only offset on the closed launcher, never the
      // open panel. This is zero on the live storefront.
      const previewOffset = parseFloat(getComputedStyle(host).getPropertyValue('--private-preview-bar-offset')) || 0;
      const offset = previewOffset - lift;
      if (Math.abs(offset) < 0.5) {
        restore(host);
        continue;
      }
      if (!saved.has(host)) {
        saved.set(host, {
          value: host.style.getPropertyValue('transform'),
          priority: host.style.getPropertyPriority('transform'),
        });
      }
      const previous = saved.get(host).value;
      const transform = `${previous && previous !== 'none' ? previous + ' ' : ''}translateY(${offset}px)`;
      if (host.style.getPropertyValue('transform') !== transform) {
        host.style.setProperty('transform', transform, 'important');
      }
      host.setAttribute('data-pf-chat-positioned', '');
    }
  }

  const stateObserver = new MutationObserver(schedule);
  const sizeObserver = new ResizeObserver(schedule);

  function bind() {
    stateObserver.disconnect();
    sizeObserver.disconnect();
    bar = document.querySelector(BAR);
    if (bar) {
      stateObserver.observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
      sizeObserver.observe(bar);
    }
    document.querySelectorAll(HOSTS + ', #chat-drawer').forEach(host => {
      stateObserver.observe(host, { attributes: true, attributeFilter: ['open', 'is-open', 'mode'] });
    });
    schedule();
  }

  function start() {
    bind();
    // Late legacy Inbox hosts are direct body additions; no document-wide
    // subtree scan, polling interval or scroll-frame geometry loop.
    new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(node =>
        node instanceof Element && (node.matches(HOSTS + ', #chat-drawer') || node.querySelector(HOSTS))
      ))) bind();
    }).observe(document.body, { childList: true });
    for (const tag of ['shopify-chat', 'shopify-agent', 'inbox-online-store-chat']) {
      customElements.whenDefined(tag).then(bind);
    }
    for (const name of ['shopify-chat:close', 'shopify-chat:enter-managed', 'shopify-chat:enter-standalone']) {
      document.addEventListener(name, schedule);
    }
    document.addEventListener('shopify:section:load', bind);
    document.addEventListener('shopify:section:unload', bind);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('pageshow', bind);
    window.addEventListener('pagehide', () => {
      saved.forEach((_, host) => restore(host));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
